import { Box, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { useEffect } from 'react';
import { useCohortDetailsModal } from '../../../core-utils/custom-hooks/useCohortDetailsModal';
import { ModalTitleBar } from '../../atoms/CohortDetailsModal/ModalTitleBar';
import { ModalBottomBar } from '../../atoms/CohortDetailsModal/ModalBottomBar';
import { useStores } from '../../../stores';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import ModalFilter from '../../atoms/CohortDetailsModal/ModalFilter';
import { buildFilterFlowData } from '../../../core-utils/helper-utils/cohortModalHelper';
import { useReportsCohortItem } from '../../../core-utils/custom-hooks/useReportsCohortItem';

interface CohortDetailsModalProps {
  open: boolean;
  handleClose: () => void;
  totalResults: number;
  isEditMode: boolean;
  existingMemberUniqueIds?: string[];
  onSave?: () => void;
}

const useStyles = makeStyles()({
  modalRoot: {
    zIndex: 500,
  },
  root: {
    width: '1120px',
    height: '591px',
    backgroundColor: patientAccessTheme.palette.status.white,
    borderRadius: '8px',
    padding: '24px 24px 24px 32px',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',

    display: 'flex',
    flexDirection: 'column',
  },
});

const CohortDetailsModal = observer(
  ({
    open,
    handleClose,
    totalResults,
    isEditMode = false,
    existingMemberUniqueIds,
    onSave,
  }: CohortDetailsModalProps) => {
    const { classes } = useStyles();

    const cohortDetailsStore = useStores().cohortDetailsStore;
    const filterFlowData = toJS(cohortDetailsStore.draftFilterFlowData);
    const isLoadingCohort = cohortDetailsStore.isLoadingCohort;

    const { getTotalMembers, toggleList } = useReportsCohortItem({
      totalResults,
      type: 'data',
      existingMemberUniqueIds,
    });

    const {
      isSaveButtonDisabled,
      handleClearFilter,
      handleSave,
      handleCancel,
      handleDeleteCohort,
      handleAddCohort,
    } = useCohortDetailsModal({
      handleClose,
      totalResults,
      onSave,
      getTotalMembers,
      toggleList,
    });

    useEffect(() => {
      if (open) {
        cohortDetailsStore.initDraft(totalResults);

        if (isEditMode) {
          const savedList = toJS(cohortDetailsStore.cohortDetailsList);
          const cohortSelectionsTypes = toJS(
            cohortDetailsStore.cohortSelectionsTypes,
          );
          const updatedFlowData = buildFilterFlowData(
            savedList,
            cohortSelectionsTypes,
            totalResults,
          );
          cohortDetailsStore.setDraftFilterFlowData(updatedFlowData);
        }
      }
    }, [open]);

    return (
      <Modal open={open} onClose={handleCancel} className={classes.modalRoot}>
        <Box className={classes.root}>
          <ModalTitleBar handleClose={handleCancel} />
          <ModalFilter
            totalResults={totalResults}
            handleDeleteCohort={handleDeleteCohort}
            handleAddCohort={handleAddCohort}
            data={filterFlowData}
            isLoading={isLoadingCohort}
            existingMemberUniqueIds={existingMemberUniqueIds}
          />
          <ModalBottomBar
            onReset={handleClearFilter}
            onCancel={handleCancel}
            onSave={handleSave}
            isSaveDisabled={isSaveButtonDisabled}
          />
        </Box>
      </Modal>
    );
  },
);

export default CohortDetailsModal;
